import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Card, CardActions, CardContent, Chip, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { campaignsPathTo } from '../utils/Api';
import SnackbarContext from '../contexts/SnackbarContext';

const statusLabel = {
  draft: 'Rascunho',
  enrollment_open: 'Inscrições abertas',
  active: 'Ativa',
  ended: 'Encerrada',
};

const statusColor = {
  draft: 'default',
  enrollment_open: 'info',
  active: 'success',
  ended: 'warning',
};

const formatDateTime = (value) => {
  if (!value) return '-';

  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const CampaignList = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { openSnackbar, openConfirmDialog } = useContext(SnackbarContext);

  const loadCampaigns = () => {
    setLoading(true);
    campaignsPathTo
      .list()
      .then((result) => setCampaigns(result.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    document.title = 'Campanhas - TudoPets.app';
    loadCampaigns();
  }, []);

  const updateCampaignInList = (campaign) => {
    setCampaigns(campaigns.map((item) => (item.id === campaign.id ? campaign : item)));
  };

  const runCampaignAction = (message, request, successMessage) => {
    openConfirmDialog(message, async () => {
      try {
        const result = await request();
        updateCampaignInList(result.data);
        openSnackbar(successMessage, 'success');
      } catch (error) {
        openSnackbar(error?.response?.data?.errors?.[0] || 'Não foi possível atualizar a campanha.', 'error');
      }
    });
  };

  const handleDelete = (campaign) => {
    openConfirmDialog('Remover esta campanha? Esta ação é irreversível.', async () => {
      try {
        await campaignsPathTo.delete(campaign.id);
        setCampaigns(campaigns.filter((item) => item.id !== campaign.id));
        openSnackbar('Campanha removida com sucesso!', 'success');
      } catch (error) {
        openSnackbar(error?.response?.data?.errors?.[0] || 'Não foi possível remover a campanha.', 'error');
      }
    });
  };

  if (loading) {
    return <Typography>Carregando campanhas...</Typography>;
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h4" color="primary">
          Campanhas
        </Typography>
        <Button variant="contained" onClick={() => navigate('/control-center/campaigns/new')}>
          Criar campanha
        </Button>
      </Box>

      <Stack spacing={2}>
        {campaigns.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Nenhuma campanha criada ainda.
          </Typography>
        )}

        {campaigns.map((campaign) => (
          <Card key={campaign.id} variant="outlined">
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                <Typography variant="h6">{campaign.name}</Typography>
                <Chip
                  label={statusLabel[campaign.status] || campaign.status}
                  color={statusColor[campaign.status] || 'default'}
                  size="small"
                />
              </Box>

              <Typography variant="body2" color="text.secondary">
                {formatDateTime(campaign.starts_at)} até {formatDateTime(campaign.ends_at)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {campaign.hidden ? 'Oculta publicamente' : 'Visível publicamente'} · {campaign.discounts_count} promoções inscritas
              </Typography>
              <Typography variant="body2" mt={1}>
                Entrega: {campaign.delivery_fee_discount_type} {campaign.delivery_fee_discount_value}
              </Typography>
              <Typography variant="body2">
                Taxa plataforma: {campaign.platform_fee_discount_type} {campaign.platform_fee_discount_value}
              </Typography>
              {campaign.required_tags.length > 0 && (
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Tags obrigatórias: {campaign.required_tags.map((tag) => tag.name).join(', ')}
                </Typography>
              )}
            </CardContent>

            <CardActions sx={{ justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button variant="outlined" onClick={() => navigate(`/control-center/campaigns/${campaign.id}`)}>
                Editar
              </Button>
              {campaign.status === 'draft' && (
                <Button
                  variant="contained"
                  onClick={() => runCampaignAction(
                    'Abrir inscrições para esta campanha?',
                    () => campaignsPathTo.openEnrollment(campaign.id),
                    'Inscrições abertas com sucesso!'
                  )}
                >
                  Abrir inscrições
                </Button>
              )}
              {campaign.status === 'enrollment_open' && (
                <Button
                  variant="contained"
                  onClick={() => runCampaignAction(
                    'Ativar esta campanha e torná-la pública?',
                    () => campaignsPathTo.activate(campaign.id),
                    'Campanha ativada com sucesso!'
                  )}
                >
                  Ativar
                </Button>
              )}
              {campaign.status === 'active' && (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => runCampaignAction(
                    'Encerrar esta campanha?',
                    () => campaignsPathTo.endCampaign(campaign.id),
                    'Campanha encerrada com sucesso!'
                  )}
                >
                  Encerrar
                </Button>
              )}
              {campaign.status !== 'active' && (
                <Button variant="outlined" color="error" onClick={() => handleDelete(campaign)}>
                  Remover
                </Button>
              )}
            </CardActions>
          </Card>
        ))}
      </Stack>
    </Box>
  );
};

export default CampaignList;
